import React, { useContext, useState } from 'react';
import { Modal, Flex, Typography, Input, Switch, message, Button, Divider } from 'antd';
import { ICompanyFile } from '@leedpulse-monorepo/interfaces';
import updateCompanyFile from '../../../../domain/use_cases/companies/updateCompanyFile';
import AuthenticationContext from '../../../../presentation/contexts/auth/AuthenticationContext';
import moment from 'moment';
import { CloseOutlined, DownloadOutlined, SaveOutlined } from '@ant-design/icons';

interface CompanyFileDetailsModalProps {
    companyFile: ICompanyFile | undefined;
    onCancel: () => void;
    onUpdateCallback: () => void;
    companyId: string;
    maxDescriptionLength?: number;
    downloadingZip: boolean;
    onDownloadZip: (companyFile: ICompanyFile) => void;
}

export default function CompanyFileDetailsModal({
    companyFile,
    onCancel,
    onUpdateCallback,
    companyId,
    maxDescriptionLength = 50,
    downloadingZip,
    onDownloadZip
}: CompanyFileDetailsModalProps) {
    const authContext = useContext(AuthenticationContext);

    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editSensitive, setEditSensitive] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);

    const handleSaveEdit = async () => {
        setSavingEdit(true);
        try {
            if (!companyFile) throw new Error('Fichier non trouvé');

            await updateCompanyFile(
                companyId,
                companyFile.id,
                { name: editName.trim(), description: editDescription, sensitive: editSensitive },
                authContext.authentication
            );
            message.success('Fichier mis à jour avec succès');
            onUpdateCallback();
        } catch (e) {
            message.error(e instanceof Error ? e.message : 'Erreur lors de la mise à jour');
        } finally {
            setSavingEdit(false);
        }
    };

    // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
    // la doc React) : dans un effet, ce recalcul coutait un second rendu.
    const [lastCompanyFile, setLastCompanyFile] = useState(companyFile);
    if (companyFile !== lastCompanyFile) {
        setLastCompanyFile(companyFile);
        if (companyFile) {
            setEditName(companyFile.name);
            setEditDescription(companyFile.description ?? '');
            setEditSensitive(companyFile.sensitive ?? false);
        }
    }

    return <Modal
        title="Détails du fichier"
        open={!!companyFile}
        onOk={handleSaveEdit}
        onCancel={onCancel}
        okText="Enregistrer"
        cancelText="Annuler"
        confirmLoading={savingEdit}
        destroyOnHidden
        footer={
            <>
                <Divider />
                <Flex gap="small" justify="space-between">
                    {companyFile &&
                        <Button
                            icon={<DownloadOutlined />}
                            loading={downloadingZip}
                            onClick={() => onDownloadZip(companyFile)}
                        >
                            Télécharger
                        </Button>
                    }

                    <Flex gap="small">
                        <Button
                            icon={<CloseOutlined />}
                            onClick={onCancel}
                        >
                            Annuler
                        </Button>

                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={savingEdit}
                            onClick={handleSaveEdit}
                        >
                            Enregistrer
                        </Button>
                    </Flex>
                </Flex>
            </>
        }
    >
        <Flex vertical gap="middle">
            <div>
                <Typography.Text type="secondary">ID</Typography.Text>
                <div><Typography.Text copyable>{companyFile?.id}</Typography.Text></div>
            </div>
            <div>
                <Typography.Text type="secondary">Date</Typography.Text>
                <div>{moment(companyFile?.timestamp).format('DD/MM/YYYY à HH:mm')}</div>
            </div>
            <div>
                <Typography.Text type="secondary">Nom du fichier</Typography.Text>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div>
                <Typography.Text type="secondary">Description</Typography.Text>
                <Input
                    maxLength={maxDescriptionLength}
                    showCount
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                />
            </div>
            <Flex align="center" gap="small">
                <Switch checked={editSensitive} onChange={setEditSensitive} />
                <Typography.Text>Sensible</Typography.Text>
            </Flex>
        </Flex>
    </Modal>;
}
